import Phaser from '../lib/phaser.js';
import { SCENE_KEYS } from './scene-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { BaseScene } from './base-scene.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from '../utils/data-manager.js';
import { SOUND_OPTIONS } from '../common/options.js';
import { AUDIO_ASSET_KEYS } from '../assets/asset-keys.js';
import { setGlobalSoundSettings, playBackgroundMusic } from '../utils/audio-utils.js';
import { UI, makeButton, makeCloseButton, makeToggle } from '../utils/ui-button.js';

// ── Settings — a clean, FULLY MOUSE/TOUCH-DRIVEN options overlay. ──
// Lets the player control audio: a master Sound switch, separate Music + SFX toggles, and a
// volume stepper. Every change is persisted to dataManager and applied immediately via
// setGlobalSoundSettings (and on reload, since preload-scene re-applies it). Opens over the
// paused world (or paused battle) and resumes it on close. Keyboard ESC still closes.

/**
 * @typedef SettingsSceneData
 * @type {object}
 * @property {string} [previousSceneName]
 */

export class SettingsScene extends BaseScene {
  /** @type {SettingsSceneData} */ #sceneData;

  constructor() {
    super({ key: SCENE_KEYS.SETTINGS_SCENE });
  }

  /** @param {SettingsSceneData} data */
  init(data) {
    super.init(data);
    this.#sceneData = data || {};
  }

  create() {
    super.create();
    this._claimOverlayInput();
    const { width, height } = this.scale;

    // dim the paused scene behind the panel
    this.add.rectangle(0, 0, width, height, 0x05080d, 0.78).setOrigin(0);

    const panelW = Math.min(560, width - 60);
    const panelH = Math.min(540, height - 60);
    const cx = width / 2;
    const cy = height / 2;
    const top = cy - panelH / 2;

    const panel = this.add.graphics();
    panel.fillStyle(UI.glass, 0.96);
    panel.fillRoundedRect(cx - panelW / 2, top, panelW, panelH, 18);
    panel.lineStyle(2, UI.emerald, 0.6);
    panel.strokeRoundedRect(cx - panelW / 2, top, panelW, panelH, 18);

    this.add
      .text(cx, top + 40, 'SETTINGS', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '40px',
        color: UI.emeraldHex,
        stroke: '#05351f',
        strokeThickness: 6,
      })
      .setOrigin(0.5);

    makeCloseButton(this, cx + panelW / 2 - 34, top + 34, () => this.#close());

    // ── Audio section ──
    let y = top + 110;
    this.add
      .text(cx - panelW / 2 + 36, y, 'AUDIO', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '18px',
        color: UI.goldHex,
      })
      .setOrigin(0, 0.5);

    const rowLabelX = cx - panelW / 2 + 36;
    const controlX = cx + panelW / 2 - 36 - 92; // toggles are width 92, right-aligned
    const rowGap = 64;

    y += 50;
    this.#row('Master Sound', rowLabelX, y);
    makeToggle(this, {
      x: controlX,
      y,
      value: this.#get(DATA_MANAGER_STORE_KEYS.OPTIONS_SOUND) !== SOUND_OPTIONS.OFF,
      onChange: (on) => {
        this.#set(DATA_MANAGER_STORE_KEYS.OPTIONS_SOUND, on ? SOUND_OPTIONS.ON : SOUND_OPTIONS.OFF);
        this.#applyAndSave();
      },
    });

    y += rowGap;
    this.#row('Music', rowLabelX, y);
    makeToggle(this, {
      x: controlX,
      y,
      value: this.#get(DATA_MANAGER_STORE_KEYS.OPTIONS_MUSIC) !== SOUND_OPTIONS.OFF,
      onChange: (on) => {
        this.#set(DATA_MANAGER_STORE_KEYS.OPTIONS_MUSIC, on ? SOUND_OPTIONS.ON : SOUND_OPTIONS.OFF);
        this.#applyAndSave();
      },
    });

    y += rowGap;
    this.#row('Sound Effects', rowLabelX, y);
    makeToggle(this, {
      x: controlX,
      y,
      value: this.#get(DATA_MANAGER_STORE_KEYS.OPTIONS_SFX) !== SOUND_OPTIONS.OFF,
      onChange: (on) => {
        this.#set(DATA_MANAGER_STORE_KEYS.OPTIONS_SFX, on ? SOUND_OPTIONS.ON : SOUND_OPTIONS.OFF);
        this.#applyAndSave();
      },
    });

    // ── Volume stepper ──
    y += rowGap;
    this.#row('Volume', rowLabelX, y);
    // Lay the stepper out as a clean "[ – ]   value   [ + ]" row. The controls live in the same
    // right-aligned 92px column as the toggles (controlX = column left edge). The − sits at the
    // left edge, the + at the right edge, and the value is centered between them — no overlap.
    // The stepper is wider than the 92px toggle column so the value never overlaps a button:
    // anchor the + at the column's right edge and extend left to fit "100%" in the middle.
    const btnSize = 36;
    const stepperW = 150;
    const plusX = controlX + 92 - btnSize / 2;
    const minusX = plusX - (stepperW - btnSize);
    const valueX = (minusX + plusX) / 2;
    makeButton(this, {
      x: minusX,
      y,
      width: btnSize,
      height: 38,
      label: '–',
      fontSize: 24,
      onClick: () => this.#stepVolume(-1),
    });
    this.#volumeText = this.add
      .text(valueX, y, '', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '20px', color: UI.textHex })
      .setOrigin(0.5);
    makeButton(this, {
      x: plusX,
      y,
      width: btnSize,
      height: 38,
      label: '+',
      fontSize: 22,
      onClick: () => this.#stepVolume(1),
    });
    this.#renderVolume();

    // status line (e.g. "Saved")
    this.#status = this.add
      .text(cx, top + panelH - 78, '', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '16px', color: UI.subHex })
      .setOrigin(0.5);

    // done button (primary)
    makeButton(this, {
      x: cx,
      y: top + panelH - 38,
      width: 220,
      height: 48,
      label: 'DONE',
      primary: true,
      onClick: () => this.#close(),
    });

    this.cameras.main.fadeIn(160, 0, 0, 0);
  }

  /** @type {Phaser.GameObjects.Text} */ #volumeText;
  /** @type {Phaser.GameObjects.Text} */ #status;

  /** @param {string} label @param {number} x @param {number} y */
  #row(label, x, y) {
    this.add
      .text(x, y, label, { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '22px', color: UI.textHex })
      .setOrigin(0, 0.5);
  }

  #get(key) {
    return dataManager.store.get(key);
  }
  #set(key, value) {
    dataManager.store.set(key, value);
  }

  /** @param {number} delta */
  #stepVolume(delta) {
    const cur = this.#get(DATA_MANAGER_STORE_KEYS.OPTIONS_VOLUME) ?? 4;
    const next = Phaser.Math.Clamp(cur + delta, 0, 4);
    if (next === cur) return;
    this.#set(DATA_MANAGER_STORE_KEYS.OPTIONS_VOLUME, next);
    this.#renderVolume();
    this.#applyAndSave();
  }

  #renderVolume() {
    const v = this.#get(DATA_MANAGER_STORE_KEYS.OPTIONS_VOLUME) ?? 4;
    this.#volumeText?.setText(`${v * 25}%`);
  }

  /** Apply settings to the live sound manager + persist them. */
  #applyAndSave() {
    setGlobalSoundSettings(this);
    // when returning to the world and music is enabled, (re)start the overworld theme so the
    // music toggle/master is felt immediately (no-op if already playing or disabled).
    const prev = this.#sceneData?.previousSceneName;
    if (!prev || prev === SCENE_KEYS.WORLD_SCENE) {
      playBackgroundMusic(this, AUDIO_ASSET_KEYS.MAIN);
    }
    dataManager.saveData();
    this.#status?.setText('Saved').setAlpha(1);
    this.tweens.add({ targets: this.#status, alpha: 0.0, delay: 900, duration: 600 });
  }

  update() {
    super.update();
    if (this._controls?.wasBackKeyPressed?.()) {
      this.#close();
    }
  }

  #close() {
    const prev = this.#sceneData?.previousSceneName ?? SCENE_KEYS.WORLD_SCENE;
    this._controls.lockInput = true;
    this.scene.stop(SCENE_KEYS.SETTINGS_SCENE);
    if (this.scene.isPaused(prev)) {
      this.scene.resume(prev);
    } else if (!this.scene.isActive(prev)) {
      this.scene.start(prev);
    }
  }
}
